/**
 * Tracking spine: consistent identity + UTMs + single track() API.
 * Generates/stores anon_id on first visit, captures and persists UTM params on landing,
 * exposes track(eventName, props) with anon_id and UTMs attached.
 */

const ANON_ID_KEY = 'digipalz_anon_id'
const UTM_STORAGE_KEY = 'digipalz_utm'
const UTM_FIRST_KEY = 'dp_utm_first'
const UTM_LAST_KEY = 'dp_utm_last'
const UTM_ATTRITION_DAYS = 30

const UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term'
]

function getOrCreateAnonId() {
  if (typeof window === 'undefined' || !window.localStorage) return null
  let id = localStorage.getItem(ANON_ID_KEY)
  if (!id) {
    id =
      'anon_' +
      Date.now().toString(36) +
      '_' +
      Math.random().toString(36).replace(/^0\./, '')
    try {
      localStorage.setItem(ANON_ID_KEY, id)
    } catch (e) {
      return null
    }
  }
  return id
}

function captureUtmFromUrl() {
  if (typeof window === 'undefined' || !window.location?.localStorage) return
  const params = new URLSearchParams(window.location.search)
  const utm = {}
  let hasAny = false
  for (const key of UTM_PARAMS) {
    const value = params.get(key)
    if (value) {
      utm[key] = value
      hasAny = true
    }
  }
  if (!hasAny) return
  utm.captured_at = new Date().toISOString()
  try {
    localStorage.setItem(UTM_LAST_KEY, JSON.stringify(utm))
    const existingFirst = localStorage.getItem(UTM_FIRST_KEY)
    if (!existingFirst) {
      localStorage.setItem(UTM_FIRST_KEY, JSON.stringify(utm))
    }
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utm))
  } catch (e) {
    // ignore
  }
}

function getDeviceType() {
  if (typeof window === 'undefined' || !window.navigator?.userAgent) return null
  const ua = window.navigator.userAgent.toLowerCase()
  if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet'
  if (
    /mobile|iphone|ipod|android.*mobile|blackberry|opera mini|iemobile/i.test(
      ua
    )
  )
    return 'mobile'
  return 'desktop'
}

function getPersistedUtm() {
  if (typeof window === 'undefined' || !window.localStorage) return null
  try {
    let raw = localStorage.getItem(UTM_LAST_KEY)
    if (!raw) raw = localStorage.getItem(UTM_STORAGE_KEY)
    if (!raw) return null
    const utm = JSON.parse(raw)
    const captured = utm.captured_at ? new Date(utm.captured_at) : null
    if (
      captured &&
      (Date.now() - captured.getTime()) / (1000 * 60 * 60 * 24) >
        UTM_ATTRITION_DAYS
    ) {
      localStorage.removeItem(UTM_LAST_KEY)
      localStorage.removeItem(UTM_STORAGE_KEY)
      return null
    }
    return utm
  } catch (e) {
    return null
  }
}

function getUtmFirst() {
  if (typeof window === 'undefined' || !window.localStorage) return null
  try {
    const raw = localStorage.getItem(UTM_FIRST_KEY)
    if (!raw) return null
    return JSON.parse(raw)
  } catch (e) {
    return null
  }
}

function getUtmLast() {
  if (typeof window === 'undefined' || !window.localStorage) return null
  try {
    const raw = localStorage.getItem(UTM_LAST_KEY)
    if (!raw) return null
    return JSON.parse(raw)
  } catch (e) {
    return null
  }
}

function runLandingCapture() {
  getOrCreateAnonId()
  captureUtmFromUrl()
}

function trackingVerify() {
  const clarityLoaded = typeof window.clarity === 'function'
  const path = window.location.pathname
  const hostname = window.location.hostname
  const anonId = getOrCreateAnonId()
  const dataLayerExists = typeof window.dataLayer !== 'undefined'
  const report = {
    clarity: clarityLoaded
      ? 'loaded (sessions will be recorded)'
      : 'not loaded (check GTM or domain)',
    page: path || '/',
    hostname,
    anon_id: anonId,
    dataLayer: dataLayerExists ? 'present (GTM)' : 'missing'
  }
  return report
}

if (typeof window !== 'undefined') {
  runLandingCapture()
  window.__digipalzSpine = {
    get anonId() {
      return getOrCreateAnonId()
    },
    get utm() {
      return getPersistedUtm()
    },
    getUtmFirst,
    getUtmLast,
    keys: {
      anon: ANON_ID_KEY,
      utm: UTM_STORAGE_KEY,
      utmFirst: UTM_FIRST_KEY,
      utmLast: UTM_LAST_KEY
    }
  }
  window.__digipalzTrackingVerify = trackingVerify
}

/**
 * Single tracking API: returns a payload with anon_id, persisted UTMs, event name, and props.
 * Does not send anywhere; callers (GTM, analytics, journey) use this payload.
 * @param {string} eventName - Event name
 * @param {object} [props] - Additional event properties
 * @returns {object} Payload with anon_id, utm_*, event, timestamp, url, and merged props
 */
function track(eventName, props = {}) {
  const anonId = getOrCreateAnonId()
  const utm = getPersistedUtm() || {}
  const utmOnly = {}
  UTM_PARAMS.forEach((k) => {
    if (utm[k] != null) utmOnly[k] = utm[k]
  })
  return {
    event: eventName,
    anon_id: anonId,
    timestamp: new Date().toISOString(),
    url: typeof window !== 'undefined' ? window.location.href : undefined,
    path: typeof window !== 'undefined' ? window.location.pathname : undefined,
    ...utmOnly,
    ...props
  }
}

export {
  getOrCreateAnonId,
  getPersistedUtm,
  getUtmFirst,
  getUtmLast,
  getDeviceType,
  captureUtmFromUrl,
  track,
  UTM_PARAMS,
  UTM_FIRST_KEY,
  UTM_LAST_KEY
}
export default {
  getOrCreateAnonId,
  getPersistedUtm,
  getUtmFirst,
  getUtmLast,
  getDeviceType,
  captureUtmFromUrl,
  track
}
